// Static FE copy for the drawer's ⓘ tooltips and inline captions. Only the dynamic caveat
// NUMBERS travel in the payload (rows[].meta.*) — the words around them live here so a wording
// change never needs a UW deploy.

import type { NaReason, ReconDegraded, ReconRowMeta } from '../../../../../store/reconciliations/types';

export const PROVISIONAL_FOOTNOTE = '*Note: Provisional metrics are updated basis unaudited data.';
// The wire flag in `recon.notes[]` that turns the footnote above on for a recon.
export const PROVISIONAL_FOOTNOTE_NOTE = 'PROVISIONAL_FOOTNOTE';

// Appended to a provisional FY row's title / aria-label.
export const PROVISIONAL_FY_TITLE_SUFFIX = ' (provisional, unaudited figures)';

export const NO_DEBIT_FOUND = 'no debit found';

// A column whose empty ("NO_DATA") cell reads better as a specific phrase than the reason-level
// one (EPFO Payments renders "no debit found", never ₹0). Keyed by column key alone — no
// recon.id lookup needed, so the shared table renderer stays generic.
export const NO_DATA_COPY_BY_COLUMN_KEY: Record<string, string> = {
    epfoPaymentsBank: NO_DEBIT_FOUND,
};

// Counts, rendered as plain integers, never scaled by the units selector.
export const COUNT_COLUMN_KEYS: ReadonlySet<string> = new Set(['bankAccounts', 'headCount']);

// The valued form of NO_DATA, column-independent because a valued NO_DATA cell always means the
// anchor is missing.
export const NO_ANCHOR_TO_COMPARE = 'No figure to compare this against';

export const RECON_CAVEATS = {
    // Vendor Payments anchor is net of credit notes.
    VENDOR_NET_OF_CREDIT_NOTES: 'Anchor is net of credit notes; can differ from the GST tab where short credit-note IDs collide.',
    // EPF wage-month basis.
    EPF_WAGE_MONTH_BASIS: 'Figures are bucketed by wage month, not payment date — a challan paid the following month still counts here.',
    // stated by each vendor column itself, so neither leg's basis depends on the other
    // leg's figure being present.
    INTRA_COMPANY_BASIS:
        'Includes supplies between the company’s own GST registrations and its group entities — the same basis as the Expense Analysis tab.',
};

// A scope filter (GST, Debt Details) that matched no recon — the only drawer empty state.
export const NO_RECONS_IN_SCOPE = 'No reconciliations to show for this view.';

export const RECONCILIATIONS_UNAVAILABLE = 'Reconciliations are unavailable right now.';

// Same rule at row level: a drill-down whose fetch failed is lost evidence, and saying "nothing to
// show" would report it as a finding about the borrower. The saga already keeps the two apart
// (fetchDrillDownFailed vs an empty rows array), so the copy must too.
export const DRILL_DOWN_UNAVAILABLE = 'No breakup available';
export const DRILL_DOWN_NOTES: Record<string, string> = {
    revenue: 'Customer split comes from GST invoices (GSTR-1) and may not add up exactly to the GSTR-3B total.',
};
export const DRILL_DOWN_FETCH_FAILED = 'Could not load the breakup — please try again.';

// the toggle moves the recon header only; the pill and the sidebar badge stay on the
// scored source. Shared so the two toggle-bearing recons can never word it differently.
const headerFollowsToggle = (scoredSource: string) =>
    `The score shown here follows the selected source; the status pill and the sidebar badge always use ${scoredSource}.`;

export const RECON_TOOLTIPS: Record<string, string> = {
    revenue: 'Revenue recognised in the financial statements, compared against GST returns and bank credits tagged to operations.',
    vendorPayments:
        'Vendor expenses per GST purchase returns (net of credit notes), compared against tagged vendor payments in bank statements.',
    accountReceivables: `Expected trade receivables (opening + revenue − collections) compared against the balance sheet closing figure. ${headerFollowsToggle(
        'GST',
    )}`,
    accountPayables: 'Expected trade payables (opening + expenses − payments) compared against the balance sheet closing figure.',
    bankBalance: 'Closing bank balance across active accounts, compared against the balance sheet cash & cash equivalents figure.',
    cashBurn: 'Net cash burn from the cash flow statement, compared against the bank-derived net movement.',
    epfStatutoryDues:
        'Salary and EPF payments in the bank statement, compared against P&L salary expense and filed EPF challans. Both pairs are narration matches — directional, not exact.',
    debt: `Projected closing debt (opening + new debt − repayments), compared against the balance sheet closing figure. ${headerFollowsToggle(
        'Bank Statement',
    )}`,
    newSanctions:
        'New sanctions per lender per FY, scored against the debt schedule. Each source is compared with the amount the debt schedule shows for that lender and year, and the year totals add up only the lenders in the debt schedule. A lender that is not in the debt schedule is listed but not scored, and if the company has no dated debt schedule this reconciliation is N/A. Lender names from every source are checked against our lender list, and names that turn out to be the same lender share one row. A name we cannot match keeps its own row, so the same lender written two different ways can appear twice, once scored against the debt schedule and once not. A loan that shows up on several bureau reports or for several directors is counted once. Lender names a source hides or leaves blank, such as "NBFC" or "BANK", are grouped as "Unidentified lender".',
    netWorth: 'Calculated net worth (opening + PAT + equity raised) compared against the balance sheet figure.',
    equity: 'Increase in share capital compared against equity raised (PAS-3 filings) and bank credits tagged to equity.',
};

// Shared between the two GSTR-3B-sourced columns (Revenue's `gstr3b`, Account Receivables'
// toggle-owned `revenueSourceGst`) — same figure, same wording, so the two entries below can
// never drift apart.
const GSTR3B_TAXABLE_TURNOVER = 'Taxable turnover declared in the GSTR-3B returns filed for the year.';

// The repayments leg of the debt-schedule projection is modelled, not read off the schedule, and
// is a lower bound wherever the schedule carries undated facilities — a known approximation the
// underwriter must see both on the combined Debt Schedule column and on the repayments leg alone
// (`repaymentsDs`), so both entries below quote this one constant rather than restating it.
const DEBT_SCHEDULE_MODELLED_CAVEAT =
    'the repayment leg is modelled, not read off the schedule. Facilities carrying no disbursal date belong to no financial year and enter no year’s figure — each cell shows their count and sanctioned total — so on a company with undated facilities this is a lower bound, and a low score can mean thin dating coverage rather than a finding.';

// R-05 — what / source / exactness for every column the 11 recons emit, keyed by column key
// exactly as NO_DATA_COPY_BY_COLUMN_KEY is, so the shared table renderer needs no recon lookup.
// Keys shared across recons (equityPas3, debtSchedule) carry copy true of both.
export const SOURCE_TOGGLE_CAPTIONS: Record<string, string> = {
    revenueSource: 'Revenue Source:',
    debtSource: 'Debt Details Source:',
};

export const COLUMN_TOOLTIPS: Record<string, string> = {
    // Revenue
    operatingInflow: 'Bank credits tagged to operations for the year. Tagging is narration-based — directional, not exact.',
    gstr3b: GSTR3B_TAXABLE_TURNOVER,
    pnlRevenue: 'Revenue as reported in the P&L statement. The other sources are scored against this figure.',
    // Vendor Payments
    paymentsBank: `Bank debits tagged to a vendor for the year. Only tagged payments count here — each cell also shows the total operating outflow, so a low score can mean thin tagging rather than a finding. ${RECON_CAVEATS.INTRA_COMPANY_BASIS}`,
    expensesGstr2a: `Purchases reported by suppliers in the GSTR-2A returns for the year. ${RECON_CAVEATS.VENDOR_NET_OF_CREDIT_NOTES} ${RECON_CAVEATS.INTRA_COMPANY_BASIS}`,
    // Account Receivables
    openingRec: 'Trade receivables on the prior year balance sheet.',
    revenueSourceGst: GSTR3B_TAXABLE_TURNOVER,
    revenueSourcePnl: 'Revenue as reported in the P&L statement.',
    collections:
        'All operating inflow in the bank statements for the year. Each cell also shows the total operating inflow and how much of it is tagged to a named customer.',
    revenueGst: 'Opening receivables plus GST revenue, less collections — what receivables should close at on the GST view.',
    revenuePnl: 'Opening receivables plus P&L revenue, less collections — what receivables should close at on the P&L view.',
    closingRec: 'Trade receivables as reported on the balance sheet. The expected receivables are scored against this figure.',
    // Account Payables
    openingPay: 'Trade payables on the prior year balance sheet.',
    expensesPnl: 'Operating costs as reported in the P&L statement (the Total Operating Cost group) — not every expense line.',
    payments: 'All operating outflow in the bank statements for the year — not only payments tagged to a vendor.',
    expectedPayables: 'Opening payables plus expenses, less payments — what payables should close at.',
    closingPay: 'Trade payables as reported on the balance sheet. The expected payables are scored against this figure.',
    // Bank Balance
    cashBs: 'Cash and cash equivalents as reported on the balance sheet. The bank closing balance is scored against this figure.',
    bankAccounts: 'How many bank accounts the closing balance is summed across.',
    closingBalanceBank:
        'Closing balance summed across the bank accounts for the year, scored against the balance sheet cash figure. Overdraft accounts are included as-is, so the total can be negative; a negative total is not scored.',
    // Cash Burn
    operatingNetCfs: 'Net cash from operating activities per the cash flow statement.',
    investingNetCfs: 'Net cash from investing activities per the cash flow statement.',
    cashBurnBank: 'Net movement across the bank accounts for the year.',
    cashBurnCfs: 'Operating plus investing net per the cash flow statement. The bank-derived movement is scored against this figure.',
    // EPF / Statutory Dues
    salaryBank: 'Bank debits whose narration reads as salary. A narration match — directional, not exact.',
    salaryPnl: 'Employee benefit expense as reported in the P&L statement. Bank salary is scored against this figure.',
    epfoPaymentsBank: `Bank debits whose narration reads as an EPFO payment. A narration match — directional, not exact: a March challan paid in April falls in a different financial year on the bank side than on the filing side. ${RECON_CAVEATS.EPF_WAGE_MONTH_BASIS}`,
    epfoFiled: `EPF challans filed with EPFO for the year, de-duplicated on TRRN. ${RECON_CAVEATS.EPF_WAGE_MONTH_BASIS}`,
    headCount: 'Employee count reported in the EPFO filings.',
    // Debt
    openingDebt: 'Closing debt on the prior year balance sheet.',
    newDebt: 'Bank credits tagged to new borrowing for the year.',
    newDebtDs: 'Facilities in the debt schedule with a disbursal date in the year.',
    repayments: 'Bank debits tagged to loan repayment for the year.',
    repaymentsDs: `Repayments for facilities in the debt schedule for the year — ${DEBT_SCHEDULE_MODELLED_CAVEAT}`,
    debtBank:
        'Opening debt plus new debt, less repayments, taken from the bank statements. Financing credits that could not be matched to debt or equity are excluded — each cell shows their total.',
    debtSchedule: `Under Debt, a projection rather than a figure the schedule states: opening debt from the balance sheet, plus the facilities in the debt schedule disbursed during this financial year, less the repayments modelled for those facilities — ${DEBT_SCHEDULE_MODELLED_CAVEAT} Under New Sanctions, the amount the debt schedule shows as sanctioned by that lender — the benchmark every other source is scored against.`,
    closingDebtBs: 'Debt as reported on the balance sheet. The projected closing debt is scored against this figure.',
    // New Sanctions
    charges: 'Charges registered with the MCA. The charge holder is often the debenture trustee rather than the lender.',
    sanctionLetters: 'Sanction amounts read from the sanction letters on record.',
    newDebtBank: 'Bank credits tagged to new borrowing from this lender.',
    directorBureau: 'Business loans on the directors’ bureau reports, by the year each was opened. Personal loans are not counted.',
    // Net-worth
    openingNetWorth: 'Net-worth on the prior year balance sheet.',
    pat: 'Profit after tax as reported in the P&L statement.',
    equityPas3: 'Equity raised per the PAS-3 filings for the year. Debentures are excluded.',
    calculatedNetWorth: 'Opening net-worth plus profit after tax plus equity raised — what net-worth should close at.',
    closingNetWorth:
        'Share capital plus reserves and other equity as reported on the balance sheet. The platform’s Shareholders’ Fund figure also includes director / shareholder loans, so the two can differ.',
    // Equity
    creditsBank:
        'Bank credits tagged to equity for the year. Financing credits that could not be matched to debt or equity are excluded — each cell shows their total.',
    increaseInShareCapital: 'Increase in share capital per the balance sheet. The equity sources are scored against this figure.',
};

const totalOperatingOutflowCaveat = (amount: string) => `Total operating outflow tagged in bank statements: ${amount}`;
const undatedFacilitiesCaveat = (count: number, sanctioned: string) =>
    `Undated facilities excluded from every year’s figure: ${count} totalling ${sanctioned} sanctioned`;
const undatedSanctionLettersCaveat = (count: number, sanctioned: string) =>
    `Sanction letters with no readable date, left out of every year’s figure: ${count} totalling ${sanctioned} sanctioned`;
// only ever stated when the server actually published the fact. An omitted
// `meta.directorBureau` means the bureau read never happened, which is a different claim.
const DIRECTORS_NOT_INGESTED = 'No directors are on record for this company, so no bureau facilities could be listed.';
const intraCompanyPaymentsCaveat = (amount: string) => `Intra-company payments this year: ${amount}`;
const intraCompanyInvoicesCaveat = (amount: string) => `Intra-company invoices this year: ${amount}`;
const totalOperatingInflowCaveat = (amount: string, customerTagged: string) =>
    `Total operating inflow tagged in bank statements: ${amount} (of which ${customerTagged} tagged to a customer)`;
const unmatchedFinancingAmountCaveat = (amount: string) => `Unmatched financing amount not tagged to debt or equity: ${amount}`;

/** Takes a raw rupee figure to the same scaled, unit-labelled string the cells render. */
type AmountFormatter = (value: number) => string;

/** Yields one caveat sentence for an FY row, or nothing when the payload carries no number for it. */
type CaveatProducer = (meta: ReconRowMeta, formatAmount: AmountFormatter) => string | undefined;

const unmatchedFinancing: CaveatProducer = (meta, formatAmount) =>
    meta.unmatchedFinancingAmount == null ? undefined : unmatchedFinancingAmountCaveat(formatAmount(meta.unmatchedFinancingAmount));

// one caveat per leg, each gated on its own figure alone, so an unavailable source on one
// leg never withholds the figure the other leg actually knows. A null still renders nothing:
// substituting ₹0 would read as "none of this was intra-company".
const intraCompanyPayments: CaveatProducer = (meta, formatAmount) =>
    meta.intraCompanyPaymentsBank == null ? undefined : intraCompanyPaymentsCaveat(formatAmount(meta.intraCompanyPaymentsBank));

const intraCompanyInvoices: CaveatProducer = (meta, formatAmount) =>
    meta.intraCompanyExpensesGstr2a == null ? undefined : intraCompanyInvoicesCaveat(formatAmount(meta.intraCompanyExpensesGstr2a));

const operatingOutflow: CaveatProducer = (meta, formatAmount) =>
    meta.totalOperatingOutflow == null ? undefined : totalOperatingOutflowCaveat(formatAmount(meta.totalOperatingOutflow));

const undatedFacilities: CaveatProducer = (meta, formatAmount) => {
    const coverage = meta.debtScheduleCoverage;
    return !coverage?.undatedRows
        ? undefined
        : undatedFacilitiesCaveat(coverage.undatedRows, formatAmount(coverage.undatedSanctionedAmount));
};

const undatedSanctionLetters: CaveatProducer = (meta, formatAmount) => {
    const coverage = meta.sanctionLetterCoverage;
    return !coverage?.undatedLetters
        ? undefined
        : undatedSanctionLettersCaveat(coverage.undatedLetters, formatAmount(coverage.undatedSanctionedAmount));
};

// Both figures are gated on the same bank source server-side, so they are present or absent together.
const operatingInflow: CaveatProducer = (meta, formatAmount) =>
    meta.totalOperatingInflow == null || meta.customerTaggedInflow == null
        ? undefined
        : totalOperatingInflowCaveat(formatAmount(meta.totalOperatingInflow), formatAmount(meta.customerTaggedInflow));

const directorsIngested: CaveatProducer = (meta) => (meta.directorBureau?.directorsIngested === false ? DIRECTORS_NOT_INGESTED : undefined);

// The per-FY half of R-05: the numbers live in rows[].meta, the sentences live above. Keyed by
// column key so the table renderer reaches them exactly as it reaches COLUMN_TOOLTIPS. A column
// can carry more than one caveat — Vendor Payments states its tagging total AND its intra-company
// disclosure — so each key holds every producer that applies to it.
const COLUMN_META_CAVEATS: Record<string, CaveatProducer[]> = {
    paymentsBank: [operatingOutflow, intraCompanyPayments],
    expensesGstr2a: [intraCompanyInvoices],
    // newDebtDs reads the same debtScheduleCoverage as debtSchedule — the undated facilities this
    // FY excludes from the combined projection are the same ones its own (dated-only) figure never
    // counted either, so the residue caveat belongs on both.
    debtSchedule: [undatedFacilities],
    newDebtDs: [undatedFacilities],
    sanctionLetters: [undatedSanctionLetters],
    directorBureau: [directorsIngested],
    collections: [operatingInflow],
    debtBank: [unmatchedFinancing],
    creditsBank: [unmatchedFinancing],
};

export const columnMetaCaveat = (columnKey: string, meta: ReconRowMeta, formatAmount: AmountFormatter): string | undefined => {
    const caveats = (COLUMN_META_CAVEATS[columnKey] ?? [])
        .map((produce) => produce(meta, formatAmount))
        .filter((caveat): caveat is string => caveat !== undefined);
    return caveats.length ? caveats.join(' ') : undefined;
};

// a missing FY−1 opening is a data fact (no prior-year balance sheet), never the
// generic "no data" phrasing NO_DATA carries. Exhaustive over NaReason on purpose: an
// uncovered reason would reach the underwriter as a bare dash, explaining nothing.
export const NA_REASON_COPY: Record<NaReason, string> = {
    NO_DATA: 'No data available',
    NO_OPENING_BALANCE: 'No prior-year balance sheet',
    // Also lands on a supplier row whose credit notes exceed its invoices. Names the anchor side,
    // never the figure rendered beside it.
    NEGATIVE_ANCHOR: 'Scored against a negative amount — no meaningful ratio',
    NEGATIVE_COMPARED: 'Net bank balance is negative (overdraft) — no meaningful ratio',
    ANCHOR_BELOW_FLOOR: 'Benchmark is zero or too small to compare — no % shown',
    SOURCE_UNAVAILABLE: 'Source unavailable — could not be checked',
    NOT_APPLICABLE: 'Not applicable for this financial year',
    NO_COUNTERPART_IN_FY: 'No GST invoice from this supplier in this financial year',
    UNATTRIBUTED_COUNTERPARTY: 'No GSTIN on this row — the supplier cannot be identified',
};

// An EMPTY NO_DATA cell reads better as a column-specific phrase where one exists (e.g. EPFO
// Payments' "no debit found"); a valued one takes NO_ANCHOR_TO_COMPARE, so the copy can never deny
// the figure beside it. Every other reason resolves to its own phrase, which applies the same way
// regardless of which column it lands on. `hasValue` has no default so a new call site that forgets
// it fails to compile. Shared by the FY table and its drill-down rows.
export function resolveNaCopy(naReason: NaReason | undefined, columnKey: string, hasValue: boolean): string | undefined {
    if (!naReason) return undefined;
    if (naReason === 'NO_DATA') return hasValue ? NO_ANCHOR_TO_COMPARE : NO_DATA_COPY_BY_COLUMN_KEY[columnKey] ?? NA_REASON_COPY.NO_DATA;
    return NA_REASON_COPY[naReason];
}

// the one glyph every degraded surface (chip, pills, drawer banner) renders, so the
// character itself can't drift between them.
export const DEGRADED_GLYPH = '⚠';

// human labels for the server's `sourceOk` vocabulary (never the raw key, never server
// copy). Keyed by the exact strings ReconciliationsService.ts emits in `degraded.unavailableSources`.
export const RECON_SOURCE_LABELS: Record<string, string> = {
    bank: 'Bank statements',
    balanceSheet: 'Balance sheet',
    pnl: 'P&L statement',
    cashFlow: 'Cash flow statement',
    gstr3b: 'GSTR-3B',
    gstr2a: 'GSTR-2A',
    accountMetrics: 'Bank account balances',
    bankCoverage: 'Bank statement coverage',
    epfo: 'EPFO filings',
    headCount: 'Headcount data',
    charges: 'ROC charges',
    fundraise: 'Fundraise (PAS-3) filings',
    debtSchedule: 'Debt schedule',
    sanctionLetters: 'Sanction letters',
    directorBureau: 'Director bureau data',
};

// An unrecognised key (a source added server-side before its FE label ships) falls back to the
// raw key rather than rendering "undefined".
export const sourceLabel = (key: string): string => RECON_SOURCE_LABELS[key] ?? key;

const formatUnavailableSources = (degraded: ReconDegraded): string => degraded.unavailableSources.map(sourceLabel).join(', ');

// Shared degraded-tooltip copy for every surface that needs only "what's missing, how much of
// the score it affected" — the chip and the 4 scope pills. The drawer banner
// additionally names the affected recons by label; see buildDegradedBannerText below.
export const buildDegradedSummary = (degraded: ReconDegraded): string => {
    const count = degraded.excludedReconIds.length;
    return `Computed on partial data — ${formatUnavailableSources(degraded)} unavailable, affecting ${count} reconciliation${
        count === 1 ? '' : 's'
    }`;
};

// Drawer banner copy — names both the unavailable sources and the recons that
// dropped out of the roll-up because of them, by label (never the raw id).
export const buildDegradedBannerText = (degraded: ReconDegraded, reconLabelById: Record<string, string>): string => {
    const recons = degraded.excludedReconIds.map((id) => reconLabelById[id] ?? id).join(', ');
    const base = `Some figures below are computed on partial data — ${formatUnavailableSources(degraded)} unavailable.`;
    return recons ? `${base} This affected: ${recons}.` : base;
};

export const EXPAND_ALL_LABEL = 'Expand All Reconciliations';
export const COLLAPSE_ALL_LABEL = 'Collapse All Reconciliations';

export const RECONCILIATIONS_LOADING = 'Loading…';

export const DATA_AVAILABILITY_COPY = {
    BUTTON: 'Data Availability',
    TITLE: 'Data availability',
    CLOSE: 'Close',
    LOADING: 'Loading…',
    FY_LABEL: 'Financial Year',
    LAST_FY: 'Last FY',
    LAST_12_MONTHS: 'Last 12 M',
    CURRENT_FY: 'Current FY',
    GST_SECTION: 'State (GSTIN)',
    BANK_GROUP: 'Bank (Account Number)',
    BANK_NAME_PLACEHOLDER: '—',
    CONNECTED: 'Connected',
    NOT_CONNECTED: 'Not Connected',
    DISCONNECTED: 'Disconnected',
    GST_EMPTY: 'No GSTs on record for this company.',
    EMPTY: 'No data is available for this period.',
    UNAVAILABLE: 'Data availability is unavailable right now.',
    RETRY: 'Retry',
    BANK_CELL_INFO: 'statement rows present for this month',
    AUDITED: 'Audited Financial',
    PROVISIONAL: 'Provisional Financial',
    NOT_AVAILABLE: 'Not available',
    AVAILABLE: 'Available',
    MONTH_FUTURE: 'Not yet due',
} as const;
